import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, BellSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import AccountTabs from "@/components/AccountTabs";
import useAccountScope from "@/hooks/useAccountScope";

export default function Subscriptions() {
  const { accounts, selectedAccountId, accountParam, selectedAccount, setSelectedAccountId } = useAccountScope();
  const [subs, setSubs] = useState([]);
  const [lastUnsubscribed, setLastUnsubscribed] = useState(null);

  const load = async () => {
    try {
      const params = accountParam ? { account_id: accountParam } : {};
      const { data } = await api.get("/subscriptions", { params });
      setSubs(data);
    } catch (e) {
      setSubs([]);
    }
  };

  useEffect(() => {
    load();
  }, [accountParam]);

  const unsub = async (id) => {
    try {
      await api.post(`/subscriptions/${id}/unsubscribe`);
      toast.success("Unsubscribed");
      setLastUnsubscribed(id);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const undo = async (id) => {
    try {
      await api.post(`/subscriptions/${id}/undo`);
      toast.success("Subscription restored");
      if (lastUnsubscribed === id) {
        setLastUnsubscribed(null);
      }
      load();
    } catch (e) {
      toast.error("Failed to undo");
    }
  };

  const active = subs.filter((s) => s.status === "active");
  const inactive = subs.filter((s) => s.status !== "active");

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <div className="mb-8">
        <div className="section-tag mb-2">/ Subscriptions</div>
        <h1 className="font-display text-3xl sm:text-4xl">Manage subscriptions</h1>
        <AccountTabs
          accounts={accounts}
          selectedAccountId={selectedAccountId}
          onSelect={setSelectedAccountId}
          className="mt-4"
        />
        <p className="text-xs uppercase tracking-widest font-mono text-muted-foreground mt-2">
          Viewing: {selectedAccount ? (selectedAccount.email || selectedAccount.account_id) : "All Accounts"}
        </p>
        <p className="text-muted-foreground mt-1">
          {active.length} active · {inactive.length} unsubscribed
        </p>
      </div>

      <div className="border border-border bg-background overflow-x-auto">
        <div className="grid grid-cols-[1fr_140px_140px_160px] gap-3 px-4 py-3 border-b border-border bg-secondary/40 text-xs uppercase tracking-widest font-mono min-w-[700px]">
          <div>Sender</div>
          <div>Category</div>
          <div className="text-right">Per month</div>
          <div className="text-right">Action</div>
        </div>

        {subs.map((s) => (
          <div
            key={s.subscription_id}
            className="grid grid-cols-[1fr_140px_140px_160px] gap-3 px-4 py-3 border-b border-border items-center text-sm min-w-[700px]"
            data-testid={`sub-row-${s.subscription_id}`}
          >
            <div>
              <div className="font-medium">{s.sender_name}</div>
              <div className="text-xs text-muted-foreground font-mono">
                {s.sender_email}
              </div>
            </div>

            <div className="text-xs uppercase tracking-widest font-mono text-muted-foreground">
              {s.category}
            </div>

            <div className="text-right font-mono">{s.frequency_per_month}</div>

            <div className="text-right">
              {s.status === "active" ? (
                <div className="flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    className="rounded-none gap-1"
                    onClick={() => unsub(s.subscription_id)}
                    data-testid={`unsub-${s.subscription_id}`}
                  >
                    <BellSlash size={14} /> Unsubscribe
                  </Button>

                  {lastUnsubscribed === s.subscription_id && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="rounded-none gap-1"
                      onClick={() => undo(s.subscription_id)}
                      data-testid={`undo-${s.subscription_id}`}
                    >
                      <Bell size={14} /> Undo
                    </Button>
                  )}
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  className="rounded-none gap-1"
                  onClick={() => undo(s.subscription_id)}
                  data-testid={`undo-${s.subscription_id}`}
                >
                  <Bell size={14} /> Undo
                </Button>
              )}
            </div>
          </div>
        ))}

        {subs.length === 0 && (
          <div className="p-12 text-center text-muted-foreground">
            No subscriptions found.
          </div>
        )}
      </div>
    </div>
  );
}
